import React, { useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(ArcElement, Tooltip, Legend);

const RentalYieldCalculator = () => {
  const [propertyValue, setPropertyValue] = useState(5000000);
  const [monthlyRent, setMonthlyRent] = useState(20000);
  const [vacancyMonths, setVacancyMonths] = useState(1);
  const [maintenanceCost, setMaintenanceCost] = useState(24000);
  const [propertyTax, setPropertyTax] = useState(10000);
  const [insurance, setInsurance] = useState(5000);
  const [managementFee, setManagementFee] = useState(0);
  const [appreciation, setAppreciation] = useState(5);
  const [holdingPeriod, setHoldingPeriod] = useState(10);
  const [result, setResult] = useState(null);

  const calculate = () => {
    // Gross Annual Rent
    const grossAnnualRent = monthlyRent * 12;
    const effectiveRent = monthlyRent * (12 - vacancyMonths);
    
    // Total Expenses
    const totalExpenses = maintenanceCost + propertyTax + insurance + managementFee;
    
    // Net Operating Income
    const netOperatingIncome = effectiveRent - totalExpenses;
    
    // Gross Rental Yield
    const grossYield = (grossAnnualRent / propertyValue) * 100;
    
    // Net Rental Yield
    const netYield = (netOperatingIncome / propertyValue) * 100;
    
    // Cash on Cash Return (assuming full cash purchase)
    const cashOnCash = netYield;
    
    // Future Property Value
    const futurePropertyValue = propertyValue * Math.pow(1 + appreciation / 100, holdingPeriod);
    const capitalGain = futurePropertyValue - propertyValue;
    
    // Total Return (rental + appreciation)
    const totalRentalIncome = netOperatingIncome * holdingPeriod;
    const totalReturn = totalRentalIncome + capitalGain;
    const annualizedReturn = (Math.pow(1 + totalReturn / propertyValue, 1 / holdingPeriod) - 1) * 100;
    
    // Price to Rent Ratio
    const priceToRent = propertyValue / grossAnnualRent;
    
    // Cap Rate (same as net yield for fully owned property)
    const capRate = netYield;

    setResult({
      grossAnnualRent,
      effectiveRent,
      totalExpenses,
      netOperatingIncome,
      grossYield,
      netYield,
      cashOnCash,
      futurePropertyValue,
      capitalGain,
      totalRentalIncome,
      totalReturn,
      annualizedReturn,
      priceToRent,
      capRate,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Rental Yield Analysis', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Description', 'Value']],
      body: [
        ['Property Value', formatCurrencyForPDF(propertyValue)],
        ['Monthly Rent', formatCurrencyForPDF(monthlyRent)],
        ['Gross Annual Rent', formatCurrencyForPDF(result.grossAnnualRent)],
        ['Effective Annual Rent', formatCurrencyForPDF(result.effectiveRent)],
        ['Annual Expenses', formatCurrencyForPDF(result.totalExpenses)],
        ['Net Operating Income', formatCurrencyForPDF(result.netOperatingIncome)],
        ['Gross Rental Yield', `${result.grossYield.toFixed(2)}%`],
        ['Net Rental Yield', `${result.netYield.toFixed(2)}%`],
        ['Cap Rate', `${result.capRate.toFixed(2)}%`],
        ['Price to Rent Ratio', `${result.priceToRent.toFixed(1)}x`],
        ['Expected Capital Gain (' + holdingPeriod + ' yrs)', formatCurrencyForPDF(result.capitalGain)],
        ['Total Annualized Return', `${result.annualizedReturn.toFixed(2)}%`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('Rental_Yield_Analysis.pdf');
  };

  const chartData = result ? {
    labels: ['Net Rental Income', 'Expenses', 'Vacancy Loss'],
    datasets: [{
      data: [
        result.netOperatingIncome,
        result.totalExpenses,
        monthlyRent * vacancyMonths
      ],
      backgroundColor: ['#10b981', '#ef4444', '#f59e0b'],
      borderWidth: 0,
    }]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="Rental Yield Calculator"
        description="Free Rental Yield Calculator - Calculate annual rental yield on property investment."
        keywords="rental yield calculator, rental return calculator, property rental yield, rental income calculator, rental ROI calculator"
        canonicalPath="/rental-yield"
      />
      <div className="calculator-header">
        <h1>🏘️ Rental Yield Calculator</h1>
        <p>Calculate ROI on your rental property investment</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Property Value (₹)</label>
              <input
                type="number"
                value={propertyValue === '' ? '' : propertyValue}
                onChange={(e) => setPropertyValue(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Monthly Rent (₹)</label>
              <input
                type="number"
                value={monthlyRent === '' ? '' : monthlyRent}
                onChange={(e) => setMonthlyRent(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Vacancy (Months per Year)</label>
            <input
              type="number"
              value={vacancyMonths === '' ? '' : vacancyMonths}
              onChange={(e) => setVacancyMonths(e.target.value === '' ? '' : Number(e.target.value))}
              max="12"
            />
          </div>

          <h4 style={{ marginTop: '16px', marginBottom: '8px', color: '#475569' }}>Annual Expenses</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Maintenance (₹/year)</label>
              <input
                type="number"
                value={maintenanceCost === '' ? '' : maintenanceCost}
                onChange={(e) => setMaintenanceCost(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Property Tax (₹/year)</label>
              <input
                type="number"
                value={propertyTax === '' ? '' : propertyTax}
                onChange={(e) => setPropertyTax(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Insurance (₹/year)</label>
              <input
                type="number"
                value={insurance === '' ? '' : insurance}
                onChange={(e) => setInsurance(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Management Fee (₹/year)</label>
              <input
                type="number"
                value={managementFee === '' ? '' : managementFee}
                onChange={(e) => setManagementFee(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <h4 style={{ marginTop: '16px', marginBottom: '8px', color: '#475569' }}>Appreciation</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Expected Appreciation (%/year)</label>
              <input
                type="number"
                step="0.5"
                value={appreciation === '' ? '' : appreciation}
                onChange={(e) => setAppreciation(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Holding Period (Years)</label>
              <input
                type="number"
                value={holdingPeriod === '' ? '' : holdingPeriod}
                onChange={(e) => setHoldingPeriod(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate Yield
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                <h3>Net Rental Yield</h3>
                <div className="amount">{result.netYield.toFixed(2)}%</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Total Annualized Return: {result.annualizedReturn.toFixed(2)}% (with appreciation)
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Gross Annual Rent</span>
                  <span className="value">{formatCurrency(result.grossAnnualRent)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Effective Rent (after vacancy)</span>
                  <span className="value">{formatCurrency(result.effectiveRent)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">Total Expenses</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.totalExpenses)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Net Operating Income</span>
                  <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.netOperatingIncome)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Gross Yield</span>
                  <span className="value">{result.grossYield.toFixed(2)}%</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Net Yield / Cap Rate</span>
                  <span className="value" style={{ color: '#10b981' }}>{result.netYield.toFixed(2)}%</span>
                </div>
                <div className="result-item">
                  <span className="label">Price to Rent Ratio</span>
                  <span className="value">{result.priceToRent.toFixed(1)}x</span>
                </div>
                
                <div style={{ marginTop: '16px', padding: '12px', background: '#dbeafe', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '8px', color: '#1e40af' }}>After {holdingPeriod} Years</h4>
                  <div className="result-item" style={{ background: 'transparent' }}>
                    <span className="label">Property Value</span>
                    <span className="value">{formatCurrency(result.futurePropertyValue)}</span>
                  </div>
                  <div className="result-item" style={{ background: 'transparent' }}>
                    <span className="label">Capital Gain</span>
                    <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.capitalGain)}</span>
                  </div>
                  <div className="result-item" style={{ background: 'transparent' }}>
                    <span className="label">Total Rental Income</span>
                    <span className="value">{formatCurrency(result.totalRentalIncome)}</span>
                  </div>
                  <div className="result-item" style={{ background: 'transparent' }}>
                    <span className="label">Total Return</span>
                    <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.totalReturn)}</span>
                  </div>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Annual Income Breakdown</h4>
                  <Pie data={chartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download Analysis PDF
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter property details to calculate rental yield</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Rental Investment Guidelines</h4>
        <ul>
          <li><strong>Gross Yield:</strong> Annual Rent / Property Value × 100</li>
          <li><strong>Net Yield:</strong> (Rent - Expenses) / Property Value × 100</li>
          <li><strong>Good rental yield:</strong> 3-5% in metro cities, 5-8% in tier-2</li>
          <li><strong>Price to Rent ratio:</strong> Below 15 is good for buying</li>
          <li>Consider capital appreciation + rental for total returns</li>
        </ul>
      </div>
    </div>
  );
};

export default RentalYieldCalculator;
